import { useMutation, useQueryClient } from '@tanstack/react-query';
import { ScansService } from '../services/scans.service';
import { CreateScanRequest } from '../common/model/dto/create-scan-request';
import { isEmpty } from 'lodash';
import { AdminScanService } from '../services/admin/admin-scan.service';
import { Scan } from '../common/model/dto/scan';

export function useAdminScanMutations() {
  const qc = useQueryClient();

  const invalidateScanQuery = () =>
    qc.invalidateQueries({ queryKey: ['admin-scans'] });

  const invalidateScanQueryById = (scanId: string) =>
    qc.invalidateQueries({ queryKey: ['admin-scan', scanId] });

  const invalidateScanQueryByUserId = () =>
    qc.invalidateQueries({ queryKey: ['admin-scans-user'] });

  const deleteScan = useMutation({
    mutationFn: async (scanId: string) => {
      return await ScansService.deleteScan(scanId);
    },
    onSuccess: () => {
      invalidateScanQuery();
      invalidateScanQueryByUserId();
    },
    onError: () => {
      invalidateScanQuery();
      invalidateScanQueryByUserId();
    },
  });

  const createScan = useMutation({
    mutationFn: async (scan: CreateScanRequest) => {
      if (!isEmpty(scan.input_image_url)) {
        return await ScansService.createScanAndProcess(scan);
      }
      return await ScansService.createScan(scan);
    },
    onSuccess: () => {
      invalidateScanQuery();
      invalidateScanQueryByUserId();
    },
    onError: () => {
      invalidateScanQuery();
      invalidateScanQueryByUserId();
    },
  });

  const processScan = useMutation({
    mutationFn: async ({
      scanId,
      toolkitVersion,
    }: {
      scanId: string;
      toolkitVersion?: string;
    }) => {
      await AdminScanService.process(scanId, toolkitVersion);
      return scanId;
    },
    onSuccess: (scanId: string) => {
      invalidateScanQueryById(scanId);
    },
    onError: () => {
      invalidateScanQuery();
      invalidateScanQueryByUserId();
    },
  });

  const updateScan = useMutation({
    mutationFn: async ({
      scanId,
      updatedProperties,
    }: {
      scanId: string;
      updatedProperties: Partial<Scan>;
    }) => {
      await ScansService.updateScan(scanId, updatedProperties);
      return scanId;
    },
    onSuccess: (scanId: string) => {
      invalidateScanQueryById(scanId);
    },
    onError: () => {
      invalidateScanQuery();
      invalidateScanQueryByUserId();
    },
  });

  return {
    deleteScan,
    createScan,
    processScan,
    updateScan,
  };
}
